/** The one owner of the `?demo=` URL shape, shared by boot (parse) and the
 *  host DemoPlayer (build) so they can never drift apart. */

export interface DemoParams {
  demo: string;
  /** Theme preset id to apply inside the demo (host mirrors its own). */
  preset: string | null;
  /** Resolved light/dark of the host (never "system" — the host resolves). */
  mode: "light" | "dark" | null;
  lang: "en" | "de" | null;
  mobile: boolean;
  /** Skip pointer glides (host reduced-motion, forwarded). */
  reduce: boolean;
  autoplay: boolean;
  /** Headless-check mode: heavily compressed pacing. */
  fast: boolean;
  loop: boolean;
}

export function parseDemoParams(search: string): DemoParams | null {
  const q = new URLSearchParams(search);
  const demo = q.get("demo");
  if (!demo) return null;
  const mode = q.get("mode");
  const lang = q.get("lang");
  return {
    demo,
    preset: q.get("preset"),
    mode: mode === "light" || mode === "dark" ? mode : null,
    lang: lang === "en" || lang === "de" ? lang : null,
    mobile: q.get("mobile") === "1",
    reduce: q.get("reduce") === "1",
    autoplay: q.get("autoplay") !== "0",
    fast: q.get("fast") === "1",
    loop: q.get("loop") === "1",
  };
}

export interface BuildDemoUrlOptions {
  preset?: string;
  mode?: "light" | "dark";
  lang?: "en" | "de";
  mobile?: boolean;
  reduce?: boolean;
  autoplay?: boolean;
  fast?: boolean;
  loop?: boolean;
}

/** Same origin + pathname as the current document with the query swapped —
 *  survives dev, static hosts, and the Tauri/Capacitor custom-scheme origins. */
export function buildDemoUrl(id: string, opts: BuildDemoUrlOptions = {}): string {
  const q = new URLSearchParams({ demo: id });
  if (opts.preset) q.set("preset", opts.preset);
  if (opts.mode) q.set("mode", opts.mode);
  if (opts.lang) q.set("lang", opts.lang);
  if (opts.mobile) q.set("mobile", "1");
  if (opts.reduce) q.set("reduce", "1");
  if (opts.autoplay === false) q.set("autoplay", "0");
  if (opts.fast) q.set("fast", "1");
  if (opts.loop) q.set("loop", "1");
  const { origin, pathname } = window.location;
  return `${origin}${pathname}?${q.toString()}`;
}
